import { MARKETING_CDN } from '../site';
import type { BlogPost } from './types';

/** The article the top announcement banner points at. */
export const REBRAND_POST_SLUG = 'koqoon-to-cultiv';

export const BLOG_POSTS: BlogPost[] = [
  {
    slug: 'koqoon-to-cultiv',
    category: 'company',
    cover: `${MARKETING_CDN}/cultiv_bg_4_grain.jpg`,
    author: 'Bennett Huyghebaert',
    authorPicture: 'https://d1oytcss5issfa.cloudfront.net/agent_picture/user-1-550e8400-e29b-41d4-a716-446655440000/Bennett_Huyghebaert.jpg',
    publishedAt: '2026-07-29',
    i18n: {
      nl: {
        title: 'Van Koqoon naar ‹Cultiv›',
        excerpt:
          'Koqoon wordt Cultiv. Waar we vandaan komen, waarom de naam verandert, en waar we naartoe gaan.',
        authorRole: 'Oprichter, Cultiv',
        body: [
          {
            t: 'lede',
            v: 'Vanaf vandaag gaat Koqoon verder onder een nieuwe naam: ‹Cultiv›. Een nieuwe naam en een nieuw jasje, maar vooral een goed moment om te vertellen waar we vandaan komen en waar we naartoe gaan.',
          },
          {
            t: 'p',
            v: 'Koqoon begon met één product: een AI-assistent die op de website van vastgoedkantoren leads genereert. Bezoekers die aan verkopen dachten, kregen meteen een gesprek en een eerste waardebepaling, en het kantoor kreeg er een verkoperlead bij. Dat werkte, en het werkt vandaag nog altijd.',
          },
          {
            t: 'img',
            src: `${MARKETING_CDN}/cultiv_team_photo.png`,
            alt: 'Het team van Cultiv (voorheen Koqoon)',
          },
          {
            t: 'p',
            v: 'Maar door met steeds meer kantoren samen te werken, leerden we dat ‹leadgeneratie maar het begin› is. Zodra de leads binnenkomen, begint het echte werk: opvolgen, herinneren, schatten, verslagen opmaken. En dat naast de bezichtigingen, de onderhandelingen en de administratie die er elk jaar bij lijkt te komen. Verkopers opvolgen vraagt veel tijd en aandacht, en net die tijd is er vaak niet.',
          },
          {
            t: 'p',
            v: 'Zo groeide het besef dat we makelaars veel verder konden helpen dan alleen bij het genereren van leads, door het ‹hele inkoopproces opnieuw uit te denken› en met AI te digitaliseren.',
          },
          {
            t: 'p',
            v: 'Dat werd ons platform. Leads zitten niet langer verspreid over mailboxen, portalen en spreadsheets, maar komen op één plaats binnen. De opvolging, het eerste wat blijft liggen in een drukke week, loopt automatisch via WhatsApp en e-mail tot de verkoper klaar is voor een afspraak. Schattingsverslagen vragen geen avondwerk meer: elke schatting steunt op vergelijkingspunten uit heel België, en AI schrijft het verslag uit in de huisstijl van het kantoor. En welke bronnen en inspanningen echt mandaten opleveren, is geen buikgevoel meer maar staat in cijfers op het dashboard.',
          },
          {
            t: 'p',
            v: 'Het doel is daarbij nooit geweest om makelaars te vervangen, integendeel. Een mandaat binnenhalen blijft mensenwerk: aan tafel zitten, vertrouwen opbouwen, onderhandelen. Wij nemen het werk errond weg, zodat ‹makelaars met meer verkopers in contact komen en meer ballen in de lucht kunnen houden› zonder dat er één valt.',
          },
          {
            t: 'p',
            v: 'Vandaar de nieuwe naam. Cultiv verwijst naar wat we elke dag voor makelaars doen: ‹elke kans cultiveren tot een mandaat›.',
          },
          {
            t: 'p',
            v: 'Vandaag werken ‹meer dan 100 kantoren› in België met ons, en dat aantal groeit elke week.',
          },
          {
            t: 'p',
            v: 'Aan iedereen die al met ons werkt: bedankt voor het vertrouwen. De naam verandert, de missie niet, en we kijken ernaar uit om samen verder te groeien.',
          },
        ],
      },
      fr: {
        title: 'De Koqoon à ‹Cultiv›',
        excerpt:
          'Koqoon devient Cultiv. D’où nous venons, pourquoi le nom change, et où nous allons.',
        authorRole: 'Fondateur, Cultiv',
        body: [
          {
            t: 'lede',
            v: 'À partir d’aujourd’hui, Koqoon poursuit son aventure sous un nouveau nom : ‹Cultiv›. Un nouveau nom et une nouvelle allure, mais surtout un bon moment pour raconter d’où nous venons et où nous allons.',
          },
          {
            t: 'p',
            v: 'Koqoon a commencé avec un seul produit : un assistant IA qui génère des leads sur les sites web des agences immobilières. Les visiteurs qui envisageaient de vendre entamaient aussitôt une conversation et recevaient une première estimation, et l’agence gagnait un lead vendeur. Ça fonctionnait, et ça fonctionne encore aujourd’hui.',
          },
          {
            t: 'img',
            src: `${MARKETING_CDN}/cultiv_team_photo.png`,
            alt: 'L’équipe Cultiv (anciennement Koqoon)',
          },
          {
            t: 'p',
            v: 'Mais en collaborant avec de plus en plus d’agences, nous avons appris que ‹la génération de leads n’est que le début›. Dès que les leads arrivent, le vrai travail commence : le suivi, les relances, les estimations, la rédaction des rapports. Et cela s’ajoute aux visites, aux négociations et à l’administration qui semble s’alourdir chaque année. Faire le suivi des vendeurs demande beaucoup de temps et d’attention, et c’est précisément ce temps qui manque souvent.',
          },
          {
            t: 'p',
            v: 'C’est ainsi qu’a mûri l’idée que nous pouvions aider les agents bien au-delà de la simple génération de leads, en ‹repensant tout le processus pour convertir un lead jusqu’au mandat signé› et en le digitalisant grâce à l’IA.',
          },
          {
            t: 'p',
            v: 'C’est devenu notre plateforme. Les leads ne sont plus éparpillés entre boîtes mail, portails et Excels, mais arrivent au même endroit. Le suivi, la première chose que l’on laisse de côté dans une semaine chargée, se fait automatiquement par WhatsApp et e-mail jusqu’à ce que le vendeur soit prêt pour un rendez-vous. Les rapports d’estimation ne demandent plus de travail de nuit : chaque estimation s’appuie sur des points de comparaison de toute la Belgique, et l’IA rédige le rapport dans le style de l’agence. Et savoir quelles sources et quels efforts génèrent réellement des mandats n’est plus une intuition, mais s’affiche en chiffres sur le tableau de bord.',
          },
          {
            t: 'p',
            v: 'L’objectif n’a jamais été de remplacer les agents, bien au contraire. Décrocher un mandat reste une affaire humaine : s’asseoir à la table, instaurer la confiance, négocier. Nous prenons en charge tout le travail qui gravite autour, pour que ‹les agents entrent en contact avec plus de vendeurs et gardent plus de balles en l’air› sans qu’aucune ne tombe.',
          },
          {
            t: 'p',
            v: 'D’où le nouveau nom. Cultiv renvoie à ce que nous faisons chaque jour pour les agents : ‹cultiver chaque opportunité jusqu’au mandat›.',
          },
          {
            t: 'p',
            v: 'Aujourd’hui, ‹plus de 100 agences› en Belgique travaillent avec nous, et ce nombre augmente chaque semaine.',
          },
          {
            t: 'p',
            v: 'À tous ceux qui travaillent déjà avec nous : merci pour votre confiance. Le nom change, la mission non, et nous avons hâte de continuer à grandir ensemble.',
          },
        ],
      },
      en: {
        title: 'From Koqoon to ‹Cultiv›',
        excerpt:
          'Koqoon is becoming Cultiv. Where we come from, why the name is changing, and where we are going.',
        authorRole: 'Founder, Cultiv',
        body: [
          {
            t: 'lede',
            v: 'As of today, Koqoon continues under a new name: ‹Cultiv›. A new name and a fresh look, but above all a good moment to share where we come from and where we’re headed.',
          },
          {
            t: 'p',
            v: 'Koqoon started with a single product: an AI assistant that generates leads on the websites of real estate agencies. Visitors thinking about selling were immediately drawn into a conversation and given a first valuation, and the agency gained a seller lead. It worked, and it still works today.',
          },
          {
            t: 'img',
            src: `${MARKETING_CDN}/cultiv_team_photo.png`,
            alt: 'The Cultiv team (formerly Koqoon)',
          },
          {
            t: 'p',
            v: 'But by working with more and more agencies, we learned that ‹lead generation is only the beginning›. Once the leads come in, the real work starts: following up, sending reminders, valuing properties, drawing up reports. And that comes on top of the viewings, the negotiations, and the administration that seems to grow every year. Following up with sellers takes a lot of time and attention, and that time is often exactly what’s missing.',
          },
          {
            t: 'p',
            v: 'That’s how we came to realize we could help agents far beyond generating leads, by ‹rethinking the entire process of converting a lead into a signed mandate› and digitizing it with AI.',
          },
          {
            t: 'p',
            v: 'That became our platform. Leads are no longer scattered across mailboxes, portals, and spreadsheets, but arrive in one place. Follow-up, the first thing to slip through the cracks in a busy week, runs automatically via WhatsApp and email until the seller is ready for a meeting. Valuation reports no longer require evening work: every valuation is built on comparable data points from across Belgium, and AI writes up the report in the agency’s own style. And which sources and efforts actually lead to mandates is no longer a gut feeling, but sits in the numbers on the dashboard.',
          },
          {
            t: 'p',
            v: 'The goal has never been to replace agents, quite the opposite. Winning a mandate remains human work: sitting down at the table, building trust, negotiating. We take away the work around it, so ‹agents can connect with more sellers and keep more balls in the air› without a single one dropping.',
          },
          {
            t: 'p',
            v: 'Hence the new name. Cultiv refers to what we do for agents every day: ‹cultivating every opportunity into a mandate›.',
          },
          {
            t: 'p',
            v: 'Today, ‹more than 100 agencies› in Belgium work with us, and that number grows every week.',
          },
          {
            t: 'p',
            v: 'To everyone already working with us: thank you for your trust. The name is changing, the mission isn’t, and we look forward to growing further together.',
          },
        ],
      },
    },
  },
];
